import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  ArrowCircleRight,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Typography,
  IconButton,
  InputAdornment,
  Box,
  CircularProgress,
  Link,
  Tooltip,
} from "@mui/material";
import {
  resetPassword,
  signOut,
  validateResetToken,
} from "../../utils/loginApi";
import { getStoredToken, removeToken } from "../../utils/auth";
import LoginTextField from "./LoginTextField";

const validationRuleStyle = (satisfied) => ({
  color: satisfied ? "#4caf50" : "#aaaaaa",
  display: "flex",
  alignItems: "center",
  fontWeight: satisfied ? "bold" : "normal",
});

const mismatchStyle = {
  color: "#ff5252",
  textAlign: "left",
  fontSize: "12px",
  mt: 0.5,
  pl: 1,
};

const serverErrorStyle = {
  color: "#ffffff",
  textAlign: "left",
  fontSize: "14px",
  mt: 2,
  p: 1,
  backgroundColor: "#d32f2f",
  borderRadius: 1,
};

export default function ResetPassword() {
  const { t, i18n } = useTranslation("common", { keyPrefix: "password" });
  const [serverError, setServerError] = useState({
    state: false,
    message: null,
  });

  // null = validation in flight, then true/false picks the screen.
  const [isTokenValid, setIsTokenValid] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const { register, handleSubmit, watch } = useForm({ mode: "onChange" });
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("reset_password_token");

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("password_confirmation");

  const hasMinLength = (passwordValue || "").length >= 6;
  const hasSymbol = /[^A-Za-z0-9\s]/.test(passwordValue || "");
  const passwordsMatch =
    (passwordValue || "").length > 0 && passwordValue === confirmPasswordValue;

  const isFormValid = hasMinLength && hasSymbol && passwordsMatch;

  const onSubmit = async (data) => {
    setServerError({ state: false, message: null });

    const response = await resetPassword({
      password: data.password,
      passwordConfirmation: data.password_confirmation,
      token: resetToken,
      locale: i18n.language,
    });

    if (response.message === "Password updated successfully.") {
      // The login screen reads this flag to show the success banner.
      window.location.replace("/login?reset_success=true");
    } else {
      setServerError({
        state: true,
        message: response.error.join(", "),
      });
    }
  };

  // A live session must be torn down before resetting: log out, then drop the
  // stored token so the new password takes a clean login.
  useEffect(() => {
    const logoutIfSignedIn = async () => {
      if (getStoredToken()) {
        await signOut();
        removeToken();
      }
    };
    logoutIfSignedIn();
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      if (!resetToken) {
        setIsTokenValid(false);
        return;
      }
      const response = await validateResetToken(resetToken);
      setIsTokenValid(response.valid === true);
    };
    validateToken();
  }, [resetToken]);

  return (
    <Box sx={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
      {isTokenValid === null && (
        <Box sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isTokenValid === false && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h5"
            sx={{ color: "#d32f2f", mb: 2, fontWeight: "bold" }}
          >
            Reset password link has expired
          </Typography>
          <Typography variant="body1" sx={{ color: "#ffffff", mb: 3 }}>
            For your security, password reset links expire after 15 minutes.
          </Typography>
          <Link
            component={RouterLink}
            to={`/forgot-password?locale=${i18n.language}`}
            underline="hover"
            sx={{ color: "primary.main", fontWeight: "bold" }}
          >
            Click here to request a new link
          </Link>
        </Box>
      )}

      {isTokenValid === true && (
        <>
          <Typography align="center" sx={{ mb: 3, fontWeight: "bold" }}>
            {t("resetPassword")}
          </Typography>

          <Box
            sx={{
              textAlign: "left",
              mb: 3,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Important Tips:
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: "20px", fontSize: "14px", color: "#cccccc" }}
            >
              <li>
                The reset link will expire in 15 minutes for security reasons.
              </li>
              <li>Use a strong password that you haven&apos;t used before.</li>
              <li>Never share your password with anyone.</li>
            </Box>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <LoginTextField
              required
              autoFocus
              type={showPassword ? "text" : "password"}
              id="new-password-required"
              label={t("enterNewPassword")}
              size="small"
              variant="filled"
              margin="dense"
              fullWidth
              {...register("password", { required: true })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: "#aaaaaa" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box sx={{ mt: 1, mb: 2, textAlign: "left", pl: 1 }}>
              <Typography
                variant="caption"
                sx={validationRuleStyle(hasMinLength)}
              >
                <Box component="span" sx={{ mr: 1 }}>
                  {hasMinLength ? "✓" : "○"}
                </Box>
                At least 6 characters
              </Typography>
              <Typography variant="caption" sx={validationRuleStyle(hasSymbol)}>
                <Box component="span" sx={{ mr: 1 }}>
                  {hasSymbol ? "✓" : "○"}
                </Box>
                Includes a symbol (e.g., ! @ # $ % & *)
              </Typography>
            </Box>

            <LoginTextField
              required
              type={showConfirmPassword ? "text" : "password"}
              id="confirm-password-required"
              label={t("confirmPassword")}
              size="small"
              variant="filled"
              margin="dense"
              fullWidth
              {...register("password_confirmation", { required: true })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                        sx={{ color: "#aaaaaa", mr: 0.5 }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>

                      <Tooltip
                        title={
                          isFormValid
                            ? "Submit new password"
                            : "Please satisfy all password conditions to submit"
                        }
                        placement="top"
                        arrow
                      >
                        <span>
                          <IconButton
                            type="submit"
                            aria-label={t("resetPassword")}
                            disabled={!isFormValid}
                            sx={{
                              p: 0,
                              "&:hover": { background: "transparent" },
                            }}
                          >
                            <ArrowCircleRight
                              color={isFormValid ? "success" : "inherit"}
                              sx={{
                                height: 50,
                                width: 40,
                                opacity: isFormValid ? 1 : 0.6,
                                color: isFormValid ? undefined : "#888888",
                              }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {(confirmPasswordValue || "").length > 0 && !passwordsMatch && (
              <Typography sx={mismatchStyle}>Passwords do not match</Typography>
            )}

            {serverError.state && (
              <Typography sx={serverErrorStyle}>
                {serverError.message}
              </Typography>
            )}
          </form>
        </>
      )}
    </Box>
  );
}
